import React, { useEffect, useMemo, useRef, useState } from "react";
import "./BusinessProfile.css";
import { supabase } from "../../supabase_client.jsx";

// for testing:
// Function to filter out empty social links
export function getActiveLinks(profile) {
  if (!profile) return [];
  const links = [profile.link1, profile.link2, profile.link3];
  return links.filter(link => link && link.trim() !== "");
}

export function formatTime(time) {
  if (!time) return "";
  const [hourStr, minute] = time.split(":");
  const hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  return `${h}:${minute} ${ampm}`;
}

// Function to determine if a specific day is marked as closed
export function isDayClosed(hours, day) {
  if (!hours || !hours[day]) return true;
  const d = hours[day];
  if (d.closed) return true;
  if (!d.open || !d.close) return true;
  return false;
}

// Function to format the display of business hours
export function formatDisplayHours(hours, day) {
  if (isDayClosed(hours, day)) return "Closed";
  return `${hours[day].open} - ${hours[day].close}`;
}

const EVENT_TYPE_OPTIONS = [
  "Food", "Sports", "Music", "Movies", "Gaming",
  "Travel", "Fitness", "Art", "Tech", "Outdoors", "Other",
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DEFAULT_HOURS = DAYS.reduce((acc, day) => {
  acc[day] = { open: "", close: "", closed: false };
  return acc;
}, {});

const BANNER_PRESETS = [
  { label: "Midnight", value: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)" },
  { label: "Forest",   value: "linear-gradient(135deg, #1b4332 0%, #2d6a4f 50%, #52b788 100%)" },
  { label: "Ember",    value: "linear-gradient(135deg, #370617 0%, #9d0208 50%, #f48c06 100%)" },
  { label: "Dusk",     value: "linear-gradient(135deg, #10002b 0%, #5a189a 50%, #e0aaff 100%)" },
  { label: "Steel",    value: "linear-gradient(135deg, #212529 0%, #495057 50%, #adb5bd 100%)" },
  { label: "Ocean",    value: "linear-gradient(135deg, #03045e 0%, #0077b6 50%, #90e0ef 100%)" },
];

export default function BusinessProfile({ currentUser, onViewSelect, businessId, readOnly }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // photo upload states
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    event_types: [],
    other_event_type: "",
    hours: DEFAULT_HOURS,
    website: "",
    additional_links: [],
    logo_url: "",
    banner_url: "",
    banner_color: BANNER_PRESETS[0].value,
    banner_type: "color", // "color" | "image"
  });

  const [newLink, setNewLink] = useState("");

  const initials = useMemo(() => {
    if (!form.name) return "B";
    return form.name.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  }, [form.name]);

  async function loadProfileByUsername(username) {
    try {
      // Step A: Find the user_id for this username
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("user_id")
        .eq("username", username)
        .single();

      if (userError || !userData) {
        console.error("User not found for username:", username);
        return null;
      }

      // Step B: Use that user_id to find the business
      const { data: bizData, error: bizError } = await supabase
        .from("businesses")
        .select("*")
        .eq("user_id", userData.user_id)
        .single();

      if (bizError && bizError.code !== "PGRST116") console.error(bizError);
      return bizData || null;
    } catch (err) {
      console.error("Lookup failed:", err);
      return null;
    }
  }
  // LOGIC CHANGE: Added function to fetch by event_owner (username) for Calendar view
  async function loadProfileByEventOwner(ownerUsername) {
    const { data, error } = await supabase
      .from("businesses")
      .select("*")
      .eq("event_owner", ownerUsername)
      .single();
    if (error && error.code !== "PGRST116") console.error(error);
    return data || null;
  }

  async function loadProfileByUserId(userId) {
    const { data, error } = await supabase
      .from("businesses")
      .select("*")
      .eq("user_id", userId)
      .single();
    if (error && error.code !== "PGRST116") console.error(error);
    return data || null;
  }

  function applyBiz(biz) {
    setProfile(biz);
    setForm({
      name: biz.name || "",
      description: biz.description || "",
      event_types: biz.event_types || [],
      other_event_type: biz.other_event_type || "",
      hours: biz.hours || DEFAULT_HOURS,
      website: biz.website || "",
      additional_links: biz.additional_links || [],
      logo_url: biz.logo_url || "",
      banner_url: biz.banner_url || "",
      banner_color: biz.banner_color || BANNER_PRESETS[0].value,
      banner_type: biz.banner_url ? "image" : "color",
    });
  }

  useEffect(() => {
    async function load() {
      setLoading(true);

      // --- LOGIC FOR VISITORS (CALENDAR VIEW) ---
      // This bridges: event_owner (username) -> user_id -> business_profile
      if (readOnly && businessId) {
        try {
          // Step 1: Find the user_id associated with that username
          const { data: userData, error: userError } = await supabase
            .from("users")
            .select("user_id")
            .eq("username", businessId)
            .single();

          if (userError || !userData) {
            console.error("Could not find user for username:", businessId);
            setLoading(false);
            return;
          }

          // Step 2: Fetch the business profile using that user_id
          const biz = await loadProfileByUserId(userData.user_id);
          if (biz) {
            applyBiz(biz);
          }
        } catch (err) {
          console.error("Error in readOnly lookup:", err);
        }
        setLoading(false);
        return;
      }

      // --- LOGIC FOR BUSINESS OWNER (EDIT VIEW) ---
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      
      if (!user) { 
        setLoading(false); 
        return; 
      }

      const biz = await loadProfileByUserId(user.id);

      if (biz) {
        applyBiz(biz);
        // Only show setup modal if the owner hasn't finished it
        if (!biz.has_setup_profile) setShowSetupModal(true);
      } else {
        // If no business profile exists yet, pre-fill the name with their username
        const { data: userData } = await supabase
          .from("users")
          .select("username")
          .eq("user_id", user.id)
          .single();

        if (userData?.username) {
          setForm(prev => ({ ...prev, name: userData.username }));
        }

        setShowSetupModal(true);
      }
      setLoading(false);
    }
    load();
  }, [businessId, readOnly]);

  function toggleEventType(type) {
    setForm(prev => {
      const cur = prev.event_types || [];
      return {
        ...prev,
        event_types: cur.includes(type) ? cur.filter(t => t !== type) : [...cur, type],
      };
    });
  }

  function updateHours(day, field, value) {
    setForm(prev => ({
      ...prev,
      hours: {
        ...prev.hours,
        [day]: { ...prev.hours[day], [field]: value },
      },
    }));
  }

  function addLink() {
    const trimmed = newLink.trim();
    if (!trimmed) return;
    setForm(prev => ({ ...prev, additional_links: [...prev.additional_links, trimmed] }));
    setNewLink("");
  }

  function removeLink(i) {
    setForm(prev => ({ ...prev, additional_links: prev.additional_links.filter((_, idx) => idx !== i) }));
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const ext = file.name.split(".").pop();
      const path = `business-logos/${userId}_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("profile-images")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("profile-images").getPublicUrl(path);
      setForm(prev => ({ ...prev, logo_url: urlData.publicUrl }));
    } catch (err) {
      alert("Logo upload failed: " + err.message);
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleBannerUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const ext = file.name.split(".").pop();
      const path = `business-banners/${userId}_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("profile-images")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("profile-images").getPublicUrl(path);
      setForm(prev => ({ ...prev, banner_url: urlData.publicUrl, banner_type: "image" }));
    } catch (err) {
      alert("Banner upload failed: " + err.message);
    } finally {
      setBannerUploading(false);
    }
  }

  async function saveProfile() {
    setSaving(true);
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) { setSaving(false); return; }

    const payload = {
      user_id: user.id,
      name: form.name.trim(),
      description: form.description.trim(),
      event_types: form.event_types,
      other_event_type: form.event_types.includes("Other") ? form.other_event_type : null,
      hours: form.hours,
      website: form.website.trim(),
      additional_links: form.additional_links,
      logo_url: form.logo_url.trim(),
      banner_url: form.banner_type === "image" ? form.banner_url : "",
      banner_color: form.banner_color,
      has_setup_profile: true,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("businesses")
      .upsert(payload, { onConflict: "user_id" })
      .select()
      .single();

    if (error) { alert("Save failed: " + error.message); setSaving(false); return; }

    setProfile(data);
    setShowSetupModal(false);
    setSetupStep(1);
    setSaving(false);
  }

  if (loading) return <div className="bpPage">Loading...</div>;

  const eventTypesDisplay = (profile?.event_types || []);
  const hoursDisplay = profile?.hours || {};
  const linksDisplay = profile?.additional_links || [];

  const bannerStyle = profile?.banner_url
    ? { backgroundImage: `url(${profile.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: profile?.banner_color || BANNER_PRESETS[0].value };

  return (
    <div className="bpPage">
      {/* Setup / Edit Modal */}
      {showSetupModal && !readOnly && (
        <div className="bpModalOverlay">
          <div className="bpModalCard">
            <button className="bpModalClose" onClick={() => setShowSetupModal(false)}>✕</button>
            <h2 className="bpModalTitle">
              {profile?.has_setup_profile ? "Edit Business Profile" : "Set Up Your Business Profile"}
            </h2>
            <p className="bpModalStep">Step {setupStep} of 4</p>

            {setupStep === 1 && (
              <div className="bpFormStep">
                <div className="bpField">
                  <label>Business Name *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. GolfPalace"
                  />
                </div>
                <div className="bpField">
                  <label>Description</label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Tell people what your business is about..."
                    rows={4}
                  />
                </div>

                <div className="bpField">
                  <label>Profile / Logo Image</label>
                  <div className="bpUploadRow">
                    <div className="bpUploadPreview bpUploadPreviewLogo">
                      {form.logo_url
                        ? <img src={form.logo_url} alt="logo preview" />
                        : <span className="bpUploadPlaceholder">{initials || "B"}</span>
                      }
                    </div>
                    <div className="bpUploadActions">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleLogoUpload}
                      />
                      <button
                        className="bpBtnUpload"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                      >
                        {logoUploading ? "Uploading…" : "Upload Image"}
                      </button>
                      {form.logo_url && (
                        <button
                          className="bpBtnUploadRemove"
                          onClick={() => setForm(prev => ({ ...prev, logo_url: "" }))}
                        >
                          Remove
                        </button>
                      )}
                      <p className="bpUploadHint">JPG, PNG or WebP. Square crop recommended.</p>
                    </div>
                  </div>
                </div>

                <div className="bpModalFooter">
                  <button
                    className="bpBtnPrimary"
                    onClick={() => setSetupStep(2)}
                    disabled={!form.name.trim()}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            {setupStep === 2 && (
              <div className="bpFormStep">
                <div className="bpField">
                  <label>Banner Style</label>
                  <div className="bpBannerTypeTabs">
                    <button
                      className={"bpBannerTypeTab" + (form.banner_type === "color" ? " active" : "")}
                      onClick={() => setForm(prev => ({ ...prev, banner_type: "color" }))}
                    >
                      🎨 Choose Color
                    </button>
                    <button
                      className={"bpBannerTypeTab" + (form.banner_type === "image" ? " active" : "")}
                      onClick={() => setForm(prev => ({ ...prev, banner_type: "image" }))}
                    >
                      🖼 Upload Photo
                    </button>
                  </div>
                </div>

                <div
                  className="bpBannerPreview"
                  style={
                    form.banner_type === "image" && form.banner_url
                      ? { backgroundImage: `url(${form.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }
                      : { background: form.banner_color }
                  }
                />

                {form.banner_type === "color" && (
                  <div className="bpField">
                    <label>Preset Colors</label>
                    <div className="bpBannerPresets">
                      {BANNER_PRESETS.map(p => (
                        <button
                          key={p.label}
                          className={"bpBannerPreset" + (form.banner_color === p.value ? " active" : "")}
                          style={{ background: p.value }}
                          title={p.label}
                          onClick={() => setForm(prev => ({ ...prev, banner_color: p.value }))}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {form.banner_type === "image" && (
                  <div className="bpField">
                    <label>Banner Photo</label>
                    <div className="bpUploadActions">
                      <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleBannerUpload}
                      />
                      <button
                        className="bpBtnUpload"
                        onClick={() => bannerInputRef.current?.click()}
                        disabled={bannerUploading}
                      >
                        {bannerUploading ? "Uploading…" : "Upload Banner Photo"}
                      </button>
                      {form.banner_url && (
                        <button
                          className="bpBtnUploadRemove"
                          onClick={() => setForm(prev => ({ ...prev, banner_url: "", banner_type: "color" }))}
                        >
                          Remove
                        </button>
                      )}
                      <p className="bpUploadHint">Wide landscape image works best (1500×400px).</p>
                    </div>
                  </div>
                )}

                <div className="bpModalFooter">
                  <button className="bpBtnSecondary" onClick={() => setSetupStep(1)}>← Back</button>
                  <button className="bpBtnPrimary" onClick={() => setSetupStep(3)}>Next →</button>
                </div>
              </div>
            )}

            {setupStep === 3 && (
              <div className="bpFormStep">
                <div className="bpField">
                  <label>Types of Events Hosted</label>
                  <div className="bpChips">
                    {EVENT_TYPE_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={"bpChip" + (form.event_types.includes(opt) ? " active" : "")}
                        onClick={() => toggleEventType(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {form.event_types.includes("Other") && (
                    <input
                      className="bpOtherInput"
                      value={form.other_event_type}
                      onChange={e => setForm({ ...form, other_event_type: e.target.value })}
                      placeholder="Describe your other event types..."
                    />
                  )}
                </div>
                <div className="bpField">
                  <label>Website</label>
                  <input
                    value={form.website}
                    onChange={e => setForm({ ...form, website: e.target.value })}
                    placeholder="https://yourbusiness.com"
                  />
                </div>
                <div className="bpField">
                  <label>Additional Links</label>
                  <div className="bpLinkRow">
                    <input
                      value={newLink}
                      onChange={e => setNewLink(e.target.value)}
                      placeholder="https://instagram.com/..."
                      onKeyDown={e => e.key === "Enter" && addLink()}
                    />
                    <button className="bpBtnAdd" onClick={addLink}>Add</button>
                  </div>
                  {form.additional_links.map((l, i) => (
                    <div key={i} className="bpLinkPill">
                      <span>{l}</span>
                      <button onClick={() => removeLink(i)}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="bpModalFooter">
                  <button className="bpBtnSecondary" onClick={() => setSetupStep(2)}>← Back</button>
                  <button className="bpBtnPrimary" onClick={() => setSetupStep(4)}>Next →</button>
                </div>
              </div>
            )}

            {setupStep === 4 && (
              <div className="bpFormStep">
                <div className="bpField">
                  <label>Hours of Operation</label>
                  <div className="bpHoursGrid">
                    {DAYS.map(day => (
                      <div key={day} className="bpHoursRow">
                        <span className="bpHoursDay">{day}</span>
                        <label className="bpClosedToggle">
                          <input
                            type="checkbox"
                            checked={form.hours[day]?.closed || false}
                            onChange={e => updateHours(day, "closed", e.target.checked)}
                          />
                          Closed
                        </label>
                        {!form.hours[day]?.closed && (
                          <>
                            <input
                              type="time"
                              value={form.hours[day]?.open || ""}
                              onChange={e => updateHours(day, "open", e.target.value)}
                            />
                            <span className="bpHoursSep">–</span>
                            <input
                              type="time"
                              value={form.hours[day]?.close || ""}
                              onChange={e => updateHours(day, "close", e.target.value)}
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bpModalFooter">
                  <button className="bpBtnSecondary" onClick={() => setSetupStep(3)}>← Back</button>
                  <button className="bpBtnPrimary" onClick={saveProfile} disabled={saving}>
                    {saving ? "Saving..." : "Save Profile ✓"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Page Shell */}
      <div className="bpShell">
        {!readOnly && (
          <div className="calendar-top-nav">
            <div className="nav-left">
              <button className="nav-tab" onClick={() => onViewSelect("businessHome")}>General</button>
              <button className="nav-tab" onClick={() => onViewSelect("business")}>Calendar</button>
              <button className="nav-tab" onClick={() => onViewSelect("businessCRMDash")}>Dashboard</button>
              <button className="nav-tab" onClick={() => onViewSelect("event-management")}>Event Management</button>
            </div>
            <div className="nav-right">
              <div className="nav-avatar-circle">
                {form.logo_url
                  ? <img src={form.logo_url} alt="profile" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                  : initials
                }
                {profile && !profile.has_setup_profile && <span className="avatar-dot" />}
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="bpHero">
          <div className="bpHeroCover" style={bannerStyle} />
          <div className="bpHeroInner">
            <div className="bpLogoWrapper">
              <div className="bpLogoCircle">
                {form.logo_url
                  ? <img src={form.logo_url} alt="logo" className="bpLogoImg" />
                  : <span>{initials}</span>
                }
              </div>
            </div>
            <div className="bpHeroText">
              <div className="bpHeroName">{profile?.name || form.name || "Your Business"}</div>
              <div className="bpHeroSub">
                {profile?.website
                  ? <a href={profile.website} target="_blank" rel="noreferrer">{profile.website}</a>
                  : "No website set"}
              </div>
              <div className="bpHeroBadges">
                <span className={"bpBadge " + (profile?.has_setup_profile ? "ok" : "warn")}>
                  {profile?.has_setup_profile ? "Profile Complete" : "Setup Required"}
                </span>
                {eventTypesDisplay.slice(0, 3).map(t => (
                  <span key={t} className="bpBadge">{t}</span>
                ))}
                {eventTypesDisplay.length > 3 && (
                  <span className="bpBadge">+{eventTypesDisplay.length - 3} more</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="bpGrid">
          <div className="bpCard wide">
            <div className="bpCardTitle">About</div>
            <div className="bpCardBody">
              <p className="bpAboutText">{profile?.description || "No description yet."}</p>
            </div>
          </div>

          <div className="bpCard">
            <div className="bpCardTitle">Event Types</div>
            <div className="bpCardBody">
              {eventTypesDisplay.length === 0
                ? <div className="bpMuted">No event types set.</div>
                : (
                  <div className="bpChipsDisplay">
                    {eventTypesDisplay.map(t => (
                      <span key={t} className="bpPill">{t}</span>
                    ))}
                    {profile?.other_event_type && (
                      <span className="bpPill other">{profile.other_event_type}</span>
                    )}
                  </div>
                )
              }
            </div>
          </div>

          <div className="bpCard">
            <div className="bpCardTitle">Hours of Operation</div>
            <div className="bpCardBody">
              {DAYS.map(day => {
                const h = hoursDisplay[day];
                if (!h) return null;
                return (
                  <div key={day} className="bpHoursDisplayRow">
                    <span className="bpHoursDisplayDay">{day}</span>
                    <span className="bpHoursDisplayTime">
                      {h.closed ? "Closed" : h.open && h.close ? `${formatTime(h.open)} – ${formatTime(h.close)}` : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bpCard">
            <div className="bpCardTitle">Links</div>
            <div className="bpCardBody">
              {profile?.website && (
                <a className="bpLink" href={profile.website} target="_blank" rel="noreferrer">
                  🌐 {profile.website}
                </a>
              )}
              {linksDisplay.length === 0 && !profile?.website
                ? <div className="bpMuted">No links added.</div>
                : linksDisplay.map((l, i) => (
                  <a key={i} className="bpLink" href={l} target="_blank" rel="noreferrer">
                    🔗 {l}
                  </a>
                ))
              }
            </div>
          </div>

          {!readOnly && (
            <div className="bpCard">
              <div className="bpCardTitle">Quick Actions</div>
              <div className="bpCardBody bpActions">
                <button className="bpActionBtn" onClick={() => { setSetupStep(1); setShowSetupModal(true); }}>
                  Edit Profile
                </button>
                <button className="bpActionBtn secondary" onClick={() => onViewSelect("event-management")}>
                  Manage Events
                </button>
                <button className="bpActionBtn secondary" onClick={() => {
                  sessionStorage.removeItem("currentUser");
                  sessionStorage.removeItem("currentView");
                  onViewSelect("loginSignup");
                }}>
                  Log Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}